import { PartialNullable } from "../../Extensions";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { ContainerControl, ContainerControlPropertyValues, IContainerControl } from "./ContainerControl";
import { ToolbarItem } from "./ToolbarItem";
import { NAMESPACE } from "./_Namespace";

import "./Toolbar.scss";

export type ToolbarPropertyValues =
    ContainerControlPropertyValues
    & {
        colorInverted: boolean;
        showLabels: boolean;
        scrollable: boolean;
    };

export type ToolbarProperties = {
    [name in keyof ToolbarPropertyValues]: Property<ToolbarPropertyValues[name]>
};

export interface IToolbar extends IContainerControl, ToolbarProperties
{
}

export class Toolbar extends ContainerControl implements IToolbar
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Toolbar";
    public static override className = "Toolbar";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ToolbarPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                colorInverted: false,
                showLabels: false,
            }
        };
    }
    public static override getAllowedChildControlClasses()
    {
        return [
            ToolbarItem
        ];
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public colorInverted!: PropertyBoolean;
    public showLabels!: PropertyBoolean;
    public scrollable!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ToolbarPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-toolbar ui icon");

        // Trigger Property Changes
        this.colorInverted.triggerChange();
        this.showLabels.triggerChange();
        this.scrollable.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _colorInvertedChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-state-color-inverted");
        else
            this.removeCssClass("rad-state-color-inverted");
    }
    protected _scrollableChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-style-scrollable");
        else
            this.removeCssClass("rad-style-scrollable");
    }
    protected _showLabelsChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-style-show-labels");
        else
            this.removeCssClass("rad-style-show-labels");
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ToolbarPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.colorInverted = new PropertyBoolean(this, propertyValues.colorInverted, this._colorInvertedChanged);
        this.showLabels = new PropertyBoolean({
            context: this,
            initialValue: propertyValues.showLabels,
            changeHandler: this._showLabelsChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Show Labels?" }),
            ],
        });
        this.scrollable = new PropertyBoolean(this, propertyValues.scrollable, this._scrollableChanged);
    }

    // #endregion

    // #endregion
}

